import { Button } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TrainingType } from "../../app/slices/TrainingSlice";
import { selectCurrentOrganizationIsWhitelisted } from "../../app/slices/UserSlice";
import PiInputfield from "../../components/PiInputField";
import { TranslatedEnvironmentDTO } from "../../dto/TranslatedEnvironmentDTO";
import { useGetLanguages } from "../../hooks/GetLanguagesHook";
import TranslatedEnvironmentView from "../translated_environments/TranslatedEnvironmentView";
import DetailsStep from "./DetailsStep";
import NewTrainingCard from "./NewTrainingCard";
import WizardStepper, { WizardStepId } from "./WizardStepper";

interface NewTrainingWizardProps {
  onCreate360: (name: string, refLanguage: string) => void;
  onCreate3D: (name: string, refLanguage: string, environmentId: string) => void;
  onCancel: () => void;
}

const ALL_STEPS: WizardStepId[] = ["type", "environment", "details"];

// 360 skips the environment step (a 360 training starts without bubbles);
// the stepper still shows it, greyed, so both types share one shape.
const STEPS: Record<TrainingType.Training360 | TrainingType.Training3D, WizardStepId[]> = {
  [TrainingType.Training360]: ["type", "details"],
  [TrainingType.Training3D]: ["type", "environment", "details"],
};

/** One wizard for both training types: Type → (Environment, 3D only) → Details. */
export default function NewTrainingWizard(props: NewTrainingWizardProps) {
  const { onCreate360, onCreate3D, onCancel } = { ...props };
  const { t } = useTranslation();
  const isWhitelisted = useSelector(selectCurrentOrganizationIsWhitelisted);
  useGetLanguages();

  const [type, setType] = useState<TrainingType.Training360 | TrainingType.Training3D>(
    TrainingType.Training360
  );
  const [step, setStep] = useState<WizardStepId>("type");
  // Held as the object, not looked up in the cached list: a search can
  // filter the picked environment out of that list.
  const [environment, setEnvironment] = useState<TranslatedEnvironmentDTO>();
  const [environmentSearch, setEnvironmentSearch] = useState("");
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("en");
  const [isCreating, setIsCreating] = useState(false);

  const is3D = type === TrainingType.Training3D;
  const steps = STEPS[type];
  const stepIndex = steps.indexOf(step);
  const environmentId = environment?.id ?? "";

  const selectType = (next: typeof type) => {
    if (next === type) return;
    setType(next);
    setEnvironment(undefined);
  };

  const goBack = () => {
    if (stepIndex <= 0) onCancel();
    else setStep(steps[stepIndex - 1]);
  };

  const goNext = () => setStep(steps[stepIndex + 1]);

  const create = () => {
    if (isCreating) return;
    setIsCreating(true);
    const trimmed = name.trim();
    if (is3D) onCreate3D(trimmed, language, environmentId);
    else onCreate360(trimmed, language);
  };

  const isLastStep = step === "details";
  const canAdvance =
    step === "type" ||
    (step === "environment" && environmentId !== "") ||
    (step === "details" && name.trim() !== "" && !isCreating);

  const typeChip = step !== "type" && (
    <span
      className="px-[6px] py-[1px] rounded-[4px] text-[12px] font-semibold"
      style={{
        color: is3D ? "var(--pi-yellow)" : "var(--pi-purple)",
        backgroundColor: `color-mix(in srgb, ${
          is3D ? "var(--pi-yellow)" : "var(--pi-purple)"
        } 14%, transparent)`,
      }}
      data-testid="new-training-type-chip"
    >
      {is3D ? "3D" : "360"}
    </span>
  );

  const typeStep = (
    <div
      className="flex flex-col h-full justify-center gap-y-[16px]"
      data-testid="new-training-step-type"
    >
      <div
        className="flex justify-center gap-[24px]"
        role="radiogroup"
        aria-label={t("new_training_wizard.steps.type") ?? ""}
      >
        <NewTrainingCard
          image="360_thumbnail.png"
          type="360"
          variant="purple"
          selected={type === TrainingType.Training360}
          onSelect={() => selectType(TrainingType.Training360)}
        />
        <NewTrainingCard
          image="3d_thumbnail.png"
          type="3D"
          variant="yellow"
          selected={is3D}
          onSelect={() => selectType(TrainingType.Training3D)}
          disabled={!isWhitelisted}
        />
      </div>
      <div className="flex items-center justify-center gap-[6px] text-[12px] text-creator-text-sub">
        <HelpOutlineIcon sx={{ fontSize: 14 }} />
        {t("new_training_wizard.format_locked")}
      </div>
    </div>
  );

  const environmentStep = (
    <div className="h-full flex flex-col gap-y-[12px]" data-testid="new-training-step-environment">
      <div className="shrink-0 self-end w-[280px]" data-testid="new-training-environment-search">
        <PiInputfield
          label=""
          placeholder={t("new_training_wizard.search_environments")}
          ariaLabel={t("new_training_wizard.search_environments") ?? ""}
          value={environmentSearch}
          onChange={setEnvironmentSearch}
          showSearchIcon
          debounceTime={300}
          autoFocus
        />
      </div>
      <div className="flex-1 min-h-0 -mx-[8px]">
        <TranslatedEnvironmentView
          selectedEnvironmentId={environmentId}
          onSelectEnvironment={setEnvironment}
          search={environmentSearch.trim()}
          ariaLabel={t("new_training_wizard.steps.environment") ?? ""}
        />
      </div>
    </div>
  );

  const detailsStep = (
    <DetailsStep
      type={type}
      environment={environment}
      name={name}
      onNameChange={setName}
      language={language}
      onLanguageChange={setLanguage}
      onChangeEnvironment={() => setStep("environment")}
    />
  );

  return (
    <div
      className="w-full max-w-[1324px] h-full max-h-[726px] flex flex-col bg-creator-surface text-creator-text border border-solid border-creator-border rounded-[10px] overflow-hidden"
      data-testid="new-training-wizard"
    >
      <div className="h-[64px] shrink-0 px-[28px] flex items-center justify-between border-b border-solid border-creator-border">
        <div className="flex items-center gap-[10px]">
          <h1 className="m-0 text-[20px] font-semibold">
            {t("new_training_wizard.title")}
          </h1>
          {typeChip}
        </div>
        <WizardStepper
          steps={ALL_STEPS}
          current={step}
          skipped={is3D ? [] : ["environment"]}
        />
      </div>

      <div className="flex-1 min-h-0 px-[28px] py-[20px]">
        {step === "type" && typeStep}
        {step === "environment" && environmentStep}
        {step === "details" && detailsStep}
      </div>

      <div className="h-[68px] shrink-0 px-[28px] flex items-center justify-between border-t border-solid border-creator-border">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-[2px] px-[8px] py-[6px] -ml-[8px] text-[14px] font-medium text-creator-text-sub hover:text-creator-text bg-transparent border-none cursor-pointer"
          data-testid={stepIndex === 0 ? "new-training-cancel" : "new-training-back"}
        >
          {stepIndex > 0 && <ChevronLeftIcon sx={{ fontSize: 18 }} />}
          {stepIndex === 0 ? t("cancel") : t("new_training_wizard.back")}
        </button>
        {isLastStep ? (
          <Button
            variant="piBlue"
            sx={{ width: "auto", minWidth: 120, height: 36, px: 2.5 }}
            onClick={create}
            disabled={!canAdvance}
            data-testid="new-training-create"
          >
            {t("new_training_wizard.create")}
          </Button>
        ) : (
          <Button
            variant="piBlue"
            sx={{ width: "auto", minWidth: 120, height: 36, px: 2.5 }}
            onClick={goNext}
            disabled={!canAdvance}
            endIcon={<ChevronRightIcon />}
            data-testid="new-training-continue"
          >
            {t("continue")}
          </Button>
        )}
      </div>
    </div>
  );
}
